import { Collapse } from '@blazemeter/blaze-ct-ui-components';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import NoParamsPlaceholder from './NoParamsPlaceholder';
import _ from 'lodash';
import DataParamItem, { GlobalParam } from './DataParamItem';
import { useDispatch, useSelector } from 'react-redux';
import SelectAllBlock from './SelectAllBlock';
import { useIsMount } from 'hooks/useIsMountHook';
import { showToastNotification } from '../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../store/ui/toasts/toastsTypes';
import { updateAssetData } from '../../../store/dataModel/dataModelActions';
import { GlobalScopeVars } from '../../../types/rest/blazeDataTypes';
import { DataDefinitionPanelBlockHeading } from '@blazemeter/blaze-data-ui';
import { setGlobalEntity } from '../../../store/dataModel/dataModelReducer';

interface GlobalScopeExtensionProps {
  serviceId: number;
}

const GlobalScopeExtension = (props: GlobalScopeExtensionProps) => {
  // For storing previous state
  // taken from: https://blog.logrocket.com/accessing-previous-props-state-react-hooks/
  const prevGlobalScopeParams = useRef<GlobalScopeVars | undefined>();

  const [isCollapsed, setIsCollapsed] = useState(true);
  const chevron = isCollapsed ? 'icon-bzm-chevron-right' : 'icon-bzm-chevron-down';
  const expanded = !isCollapsed ? 'panel-block-expandable-expanded' : undefined;
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [selectedParams, setSelectedParams] = useState([]);
  const isMount = useIsMount();
  const globalScopeParams = useSelector((state: any) => state.dataModel.globalEntity);
  const globalEntityAssetId = useSelector((state: any) => state.dataModel.globalEntityAssetId);
  const dispatch = useDispatch();

  const setGlobalScopeParams = (globalScope: any) => {
    dispatch(setGlobalEntity(globalScope));
  };

  /**
   * Expands the global entity if there is any data.
   */
  const expandOnMount = async () => {
    if (!_.isNil(globalScopeParams) && _.size(globalScopeParams) !== 0) {
      setIsCollapsed(false);
    }
  };

  //Initial on mount load
  useEffect(() => {
    if (isMount) {
      expandOnMount();
    }
  }, [props.serviceId]);

  useEffect(() => {
    if (!isMount && !_.isNil(globalEntityAssetId)) {
      persistGlobalEntity();
    }
  }, [globalScopeParams]);

  /**
   * Saves Global Entity to asset repository.
   */
  const persistGlobalEntity = async () => {
    if (_.isNil(globalEntityAssetId)) {
      //Dispatch error
      return;
    }

    // If previous value and current value are same, no need to save it
    if (_.isEqual(prevGlobalScopeParams.current, globalScopeParams)) {
      return;
    }

    // Only update in case there is some change
    await updateAssetData(globalEntityAssetId, globalScopeParams);
    prevGlobalScopeParams.current = globalScopeParams;
  };

  const onDeleteHandler = (key: string) => {
    const accept = window.confirm(`Are you sure you want to delete parameter '${key}'?`);
    if (!accept) {
      return;
    }

    const updated = _.cloneDeep(globalScopeParams);
    delete updated[key];
    setGlobalScopeParams(updated);
  };

  const onSelectHandler = (key: string) => {
    let updated = _.cloneDeep(selectedParams);
    const isContained = _.findIndex(selectedParams, item => item === key) !== -1;
    if (!isContained) {
      updated.push(key);
    } else {
      updated = _.filter(updated, item => key !== item);
    }
    setSelectedParams(updated);
  };

  const onDeleteAll = () => {
    const accept = window.confirm(`Are you sure you want to delete all selected parameters?`);
    if (!accept) {
      return;
    }
    setSelectedParams([]);
    setGlobalScopeParams({});
  };

  /**
   * Save param to the global param field. We also provide old name in case user changes the
   * value.
   * @param param
   * @param oldName
   */
  const saveParam = (param: GlobalParam, oldName?: string) => {
    const globalParams = _.cloneDeep(globalScopeParams);
    const { name, value } = param;

    // If old name is missing, it means we are adding new item
    if (_.isNil(oldName)) {
      setIsAddingNew(false);
      dispatch(showToastNotification(ToastType.Success, 'New Global Scope parameter has been added.'));
    }

    //Name has changed, delete old key
    if (oldName !== name) {
      delete globalParams[oldName];
    }
    globalParams[param.name] = value;
    setGlobalScopeParams(globalParams);
  };

  const getItems = () => {
    if (_.isEmpty(globalScopeParams) && !isAddingNew) {
      return <NoParamsPlaceholder />;
    }

    const items: any[] = [];

    if (!_.isEmpty(selectedParams)) {
      items.push(
        <SelectAllBlock
          key={'selectAll'}
          onDeleteAll={onDeleteAll}
          onDeselect={() => {
            setSelectedParams([]);
          }}
          isChecked={!_.isEmpty(selectedParams)}
        />,
      );
    }

    const paramNames = globalScopeParams ? _.map(globalScopeParams, (item, key) => key) : [];

    if (isAddingNew) {
      items.push(
        <DataParamItem
          key={'new_item'}
          onDelete={() => {
            setIsAddingNew(false);
          }}
          isChecked={false}
          isNew={true}
          param={{ name: undefined, value: undefined }}
          onSave={item => saveParam(item)}
          existingParams={paramNames}
        />,
      );
    }

    return _.concat(
      items,
      _.map(globalScopeParams, (value, key) => (
        <DataParamItem
          onSelect={() => {
            onSelectHandler(key);
          }}
          onDelete={() => onDeleteHandler(key)}
          key={key}
          isNew={false}
          param={{ name: key, value: value }}
          onSave={item => saveParam(item, key)}
          isChecked={_.findIndex(selectedParams, item => item === key) !== -1}
          existingParams={paramNames.filter(item => item !== key)}
        />
      )),
    );
  };

  const addNewParameterClicked = () => {
    if (isCollapsed) {
      setIsCollapsed(false);
    }
    setIsAddingNew(!isAddingNew);
  };

  // Do not show panel if there is no global scope
  if (_.isNil(globalScopeParams)) {
    return null;
  }

  return (
    <>
      <DataDefinitionPanelBlockHeading heading="Global scope" />
      <div className={clsx('panel-block', 'panel-block-expandable', expanded)}>
        <div className="d-flex align-items-center justify-content-between w-100">
          <Button
            className={'btn btn-link-dark btn-no-hover'}
            onClick={() => {
              setIsCollapsed(!isCollapsed);
            }}
          >
            <span className={clsx(chevron, 'icon-xs align-middle text-primary')} aria-hidden="true"></span>
            <strong className={'font-weight-semi-bold align-middle'}>Data Parameters</strong>
          </Button>
          <div className={'float-right d-flex entity-section'}>
            <button
              className="btn btn-no-hover btn-primary btn-circular btn-sm"
              title="Add Data Parameter"
              onClick={addNewParameterClicked}
            >
              <span className="icon-bzm-add" aria-hidden="true"></span>
            </button>
          </div>
        </div>
      </div>
      <Collapse isCollapsed={isCollapsed} id="data-parameters">
        {getItems}
      </Collapse>
    </>
  );
};

export default GlobalScopeExtension;
